import { Box, BoxProps } from '@everlywell/ui-kit';
import React from 'react';
import type { ReactNode, Ref, VideoHTMLAttributes } from 'react';

type VideoSource = {
  src: string;
  type?: string;
};

type BaseVideoProps = {
  containerSx?: BoxProps['sx'];
  htmlVideoProps?: VideoHTMLAttributes<HTMLVideoElement>;
  videoRef?: Ref<HTMLVideoElement>;
  children?: ReactNode;
};

type MultipleSourcesProps = BaseVideoProps & {
  sources: VideoSource[];
  url?: never;
  contentType?: never;
};

type SingleSourceProps = BaseVideoProps & {
  sources?: never;
  url: string;
  contentType?: string;
};

export type VideoComponentProps = MultipleSourcesProps | SingleSourceProps;

const hasMultipleSources = (
  props: VideoComponentProps,
): props is MultipleSourcesProps =>
  Array.isArray((props as MultipleSourcesProps).sources);

const VideoComponent = (props: VideoComponentProps) => {
  const { containerSx, htmlVideoProps, videoRef, children } = props;

  const resolvedSources: VideoSource[] = hasMultipleSources(props)
    ? props.sources
    : [{ src: props.url, type: props.contentType }];

  // Muted videos have no audio content and cannot have meaningful captions.
  // Hide them from assistive technology unless the caller has already opted
  // in to an accessible label (aria-label / aria-labelledby) or explicitly
  // overridden aria-hidden.
  const hasExplicitAriaAttr =
    htmlVideoProps?.['aria-hidden'] !== undefined ||
    htmlVideoProps?.['aria-label'] !== undefined ||
    htmlVideoProps?.['aria-labelledby'] !== undefined;

  const resolvedVideoProps: VideoHTMLAttributes<HTMLVideoElement> = {
    ...(htmlVideoProps?.muted && !hasExplicitAriaAttr
      ? { 'aria-hidden': true }
      : {}),
    ...htmlVideoProps,
  };

  return (
    <Box sx={containerSx}>
      <video ref={videoRef} {...resolvedVideoProps}>
        {resolvedSources.map(({ src, type }, index) => (
          <source
            key={`${src}-${type ?? 'source'}-${index}`}
            src={src}
            type={type}
          />
        ))}
        {children}
        Your browser does not support the video tag.
      </video>
    </Box>
  );
};

export default VideoComponent;
